import React, { FC, useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { CirclePlus, X } from 'lucide-react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { Form, FormControl, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useScrollOverflow } from '@/hooks/scroll/use-scroll-overflow';
import { useFieldArrayKeyboard } from '@/hooks/use-field-array-keyboard';
import { ModalProps } from '@/hooks/use-modal';
import { cn } from '@/lib/utils';

import { useCreateRequestedIntegrations } from '../../hooks/use-create-requested-integrations';
import { createRequestFormSchema, RequestFormValues } from '../../schemas/request-schema';

interface RequestModalProps extends ModalProps {}

export const RequestModal: FC<RequestModalProps> = ({ onClose, ...props }) => {
  const { t } = useTranslation(['authenticated.onboarding.integrations']);

  const requestFormSchema = createRequestFormSchema(t);
  const form = useForm<RequestFormValues>({
    resolver: zodResolver(requestFormSchema),
    defaultValues: {
      tools: [
        {
          name: '',
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'tools',
    keyName: 'toolID',
  });
  const { ref: scrollRef, isScrollable } = useScrollOverflow([fields.length]);

  const toolsValue = useWatch({
    name: 'tools',
    control: form.control,
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const createRequestedIntegrationsMutation = useCreateRequestedIntegrations({
    onSuccess: () => {
      handleClose();
    },
  });

  const handleSubmit = async (values: RequestFormValues) => {
    createRequestedIntegrationsMutation.mutate({
      integrations: values.tools.map(tool => ({
        nameOrLink: tool.name,
      })),
    });
  };

  const appendTool = useCallback(() => {
    append({ name: '' });
  }, [append]);

  const { formRef, handleKeyDown } = useFieldArrayKeyboard({
    rowCount: fields.length,
    onAppendRow: appendTool,
    onSubmit: useCallback(() => form.handleSubmit(handleSubmit)(), [form, handleSubmit]),
    arrayFieldName: 'tools',
    firstFieldName: 'name',
  });

  return (
    <Modal onClose={handleClose} className="flex max-h-[85vh] max-w-md flex-col" {...props}>
      <Form {...form}>
        <form
          ref={formRef}
          onSubmit={form.handleSubmit(handleSubmit)}
          onKeyDown={handleKeyDown}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
        >
          <Modal.Title className={cn('mb-0 shrink-0 pb-4 pr-8', isScrollable && 'border-b')}>
            {t('modals.request.title')}
            <p className="mt-1.5 text-sm font-normal text-muted-foreground">
              {t('modals.request.description')}
            </p>
          </Modal.Title>

          <Modal.Body className="min-h-0 flex-1 overflow-hidden py-4">
            <div ref={scrollRef} className="space-y-4 overflow-y-auto px-1">
              <FormItem>
                <FormLabel>{t('fields.tool.label')}</FormLabel>

                {fields.map((field, index) => (
                  <div key={field.toolID} className="flex items-start gap-2">
                    <FormControl>
                      <Input
                        placeholder={t('fields.tool.placeholder')}
                        {...form.register(`tools.${index}.name`)}
                      />
                    </FormControl>
                    {fields.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        className="shrink-0"
                        onClick={() => remove(index)}
                        disabled={index === 0}
                        style={{ visibility: index === 0 ? 'hidden' : 'visible' }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </FormItem>

              <Button variant="ghost" className="w-fit" type="button" onClick={appendTool}>
                <CirclePlus className="h-4 w-4" />
                {t('modals.request.actions.addMore')}
              </Button>
            </div>
          </Modal.Body>

          <Modal.Footer className={cn('mt-0 shrink-0 pt-4', isScrollable && 'border-t')}>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={createRequestedIntegrationsMutation.isPending}
            >
              {t('actions.cancel')}
            </Button>

            <ButtonLoadable
              isLoading={createRequestedIntegrationsMutation.isPending}
              disabled={!toolsValue[0].name}
            >
              {createRequestedIntegrationsMutation.isPending
                ? t('actions.sending')
                : t('actions.send')}
            </ButtonLoadable>
          </Modal.Footer>
        </form>
      </Form>
    </Modal>
  );
};
